import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

const INSTAGRAM_URL = 'https://www.instagram.com/t.yokota0809/';
const LINE_URL = 'https://line.me/ti/p/9_7o9hrucX';

export default function Footer() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  const scrollToSection = (id: string) => {
    if (!isHomePage) {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer
      className="relative"
      style={{
        background: 'linear-gradient(135deg, #f5e6c8 0%, #ede0c4 40%, #e8d5a3 100%)',
        fontFamily: "'Noto Serif JP', serif",
      }}
    >
      {/* 上部 市松模様装飾ライン */}
      <div className="w-full h-4 flex overflow-hidden">
        {Array.from({ length: 80 }).map((_, i) => (
          <div
            key={i}
            className="flex-shrink-0 w-8 h-4"
            style={{
              backgroundColor: i % 2 === 0 ? '#8B2500' : '#c8a96e',
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* 朱色の枠線 */}
        <div
          className="relative p-8 rounded"
          style={{ border: '2px solid #8B2500' }}
        >
          {/* 四隅の装飾 */}
          <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2" style={{ borderColor: '#8B2500' }} />
          <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2" style={{ borderColor: '#8B2500' }} />
          <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2" style={{ borderColor: '#8B2500' }} />
          <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2" style={{ borderColor: '#8B2500' }} />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* ロゴ・説明 */}
            <div>
              <img
                src="https://public.readdy.ai/ai/img_res/de640276-e1f2-4aaf-8379-d8e42afbd5f8.png"
                alt="KOMINKA - Traditional Japanese House"
                className="h-16 w-auto mb-4"
              />
              <p className="text-sm leading-relaxed" style={{ color: '#4a3728' }}>
                {i18n.language === 'ja' 
                  ? '東京・大田区馬込の昭和レトロな古民家。1970年代の日本を感じられる一棟貸し宿。最大8名収容。'
                  : 'Showa Retro traditional house in Magome, Tokyo. Experience 1970s Japan. Entire house rental for up to 8 guests.'}
              </p>
            </div>

            {/* クイックリンク */}
            <div>
              <h3
                className="text-base font-semibold mb-4 flex items-center gap-2"
                style={{ color: '#8B2500' }}
              >
                <span style={{ color: '#8B2500' }}>◆</span>
                {i18n.language === 'ja' ? 'クイックリンク' : 'Quick Links'}
              </h3>
              <div className="space-y-2">
                {[
                  { label: t('common:nav.home'), action: () => scrollToSection('home') },
                  { label: t('common:nav.rooms'), action: () => scrollToSection('rooms') },
                  { label: t('common:nav.access'), action: () => scrollToSection('access') },
                  { label: t('common:nav.reservation'), action: () => scrollToSection('reservation') },
                  { label: 'Instagram', action: () => window.open(INSTAGRAM_URL, '_blank') },
                ].map((item) => (
                  <button
                    key={item.label}
                    onClick={item.action}
                    className="block text-sm transition-colors whitespace-nowrap cursor-pointer hover:underline"
                    style={{ color: '#4a3728' }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#8B2500')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#4a3728')}
                  >
                    › {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* アドレス・アクセス情報 */}
            <div>
              <h3
                className="text-base font-semibold mb-4 flex items-center gap-2"
                style={{ color: '#8B2500' }}
              >
                <span style={{ color: '#8B2500' }}>◆</span>
                {i18n.language === 'ja' ? 'アクセス' : 'Access'}
              </h3>
              <address className="not-italic space-y-3">
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 w-4 h-4 flex items-center justify-center flex-shrink-0" style={{ color: '#8B2500' }}>
                    <i className="ri-map-pin-2-line text-sm"></i>
                  </span>
                  <div>
                    <p className="text-sm leading-snug" style={{ color: '#4a3728' }}>
                      {i18n.language === 'ja' 
                        ? '東京都大田区東馬込1-12-12'
                        : '1-12-12 Higashi-Magome, Ota City, Tokyo'}
                    </p>
                    <p className="text-xs leading-snug mt-0.5" style={{ color: '#6b4c2a' }}>
                      {i18n.language === 'ja' 
                        ? '〒143-0022'
                        : '〒143-0022 Japan'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 w-4 h-4 flex items-center justify-center flex-shrink-0" style={{ color: '#8B2500' }}>
                    <i className="ri-train-line text-sm"></i>
                  </span>
                  <p className="text-sm leading-snug" style={{ color: '#4a3728' }}>
                    {i18n.language === 'ja' 
                      ? '都営浅草線「馬込駅」より徒歩3分'
                      : '3 min walk from Magome Station (Toei Asakusa Line)'}
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 w-4 h-4 flex items-center justify-center flex-shrink-0" style={{ color: '#8B2500' }}>
                    <i className="ri-plane-line text-sm"></i>
                  </span>
                  <p className="text-sm leading-snug" style={{ color: '#4a3728' }}>
                    {i18n.language === 'ja' 
                      ? '羽田空港から電車で約30分'
                      : '30 min from Haneda Airport by train'}
                  </p>
                </div>
                <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(139,37,0,0.2)' }}>
                  <a
                    href="https://www.google.com/maps/search/1-12-12+Higashi-Magome,+Ota+City,+Tokyo"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded transition-colors whitespace-nowrap cursor-pointer"
                    style={{ backgroundColor: '#8B2500', color: '#fdf6e3' }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#6b1c00')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#8B2500')}
                  >
                    <i className="ri-map-pin-line"></i>
                    Google Maps
                  </a>
                </div>
              </address>
            </div>

            {/* SNS */}
            <div>
              <h3
                className="text-base font-semibold mb-4 flex items-center gap-2"
                style={{ color: '#8B2500' }}
              >
                <span style={{ color: '#8B2500' }}>◆</span>
                {i18n.language === 'ja' ? 'フォローする' : 'Follow Us'}
              </h3>

              <div className="flex flex-col gap-2.5">
                {[
                  {
                    href: INSTAGRAM_URL,
                    icon: 'ri-instagram-line',
                    label: 'Instagram',
                    sub: '@t.yokota0809',
                    gradient: 'linear-gradient(135deg, #f09433, #dc2743, #bc1888)',
                    shadow: 'rgba(220,39,67,0.25)',
                  },
                  {
                    href: LINE_URL,
                    icon: 'ri-line-fill',
                    label: 'LINE',
                    sub: i18n.language === 'ja' ? '友だち追加' : 'Add Friend',
                    gradient: '#06C755',
                    shadow: 'rgba(6,199,85,0.25)',
                  },
                ].map((sns) => (
                  <a
                    key={sns.label}
                    href={sns.href}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="flex items-center gap-3 px-3 py-2 rounded-lg transition-all hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
                    style={{
                      background: 'rgba(255,248,230,0.8)',
                      border: '1.5px solid #c8a96e',
                      boxShadow: `0 2px 6px ${sns.shadow}`,
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(255,248,230,1)';
                      (e.currentTarget as HTMLElement).style.borderColor = '#8B2500';
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(255,248,230,0.8)';
                      (e.currentTarget as HTMLElement).style.borderColor = '#c8a96e';
                    }}
                  >
                    <div
                      className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0"
                      style={{ background: sns.gradient }}
                    >
                      <i className={`${sns.icon} text-white text-sm`}></i>
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-xs font-bold leading-tight" style={{ color: '#3d1f00' }}>{sns.label}</span>
                      <span className="text-xs leading-tight truncate" style={{ color: '#9a7a50' }}>{sns.sub}</span>
                    </div>
                    <span className="ml-auto w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-external-link-line text-xs" style={{ color: '#c8a96e' }}></i>
                    </span>
                  </a>
                ))}
              </div>

              {/* 装飾的な家紋風モチーフ */}
              <div className="mt-5 flex items-center gap-2">
                <div className="flex-1 h-px" style={{ backgroundColor: '#8B2500', opacity: 0.4 }} />
                <span className="text-lg" style={{ color: '#8B2500' }}>✦</span>
                <div className="flex-1 h-px" style={{ backgroundColor: '#8B2500', opacity: 0.4 }} />
              </div>
            </div>
          </div>

          {/* お支払い方法 */}
          <div className="mt-8 pt-6" style={{ borderTop: '1px solid #c8a96e' }}>
            <div className="flex flex-col items-center gap-4">
              {/* カードブランド */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <p className="text-sm font-semibold whitespace-nowrap" style={{ color: '#8B2500' }}>
                  {i18n.language === 'ja' ? 'ご利用可能なカード' : 'Accepted Cards'}
                </p>
                <div className="flex items-center gap-3 flex-wrap justify-center">
                  {/* Visa */}
                  <div className="flex items-center justify-center px-3 py-1.5 rounded-md bg-white shadow-sm border" style={{ borderColor: '#c8a96e', minWidth: '60px', height: '32px' }}>
                    <span className="font-extrabold text-sm tracking-tight" style={{ color: '#1A1F71', fontFamily: 'Arial, sans-serif', letterSpacing: '-0.5px' }}>VISA</span>
                  </div>
                  {/* Mastercard */}
                  <div className="flex items-center justify-center gap-0.5 px-3 py-1.5 rounded-md bg-white shadow-sm border" style={{ borderColor: '#c8a96e', minWidth: '60px', height: '32px' }}>
                    <div className="w-5 h-5 rounded-full" style={{ backgroundColor: '#EB001B', opacity: 0.95 }}></div>
                    <div className="w-5 h-5 rounded-full -ml-2.5" style={{ backgroundColor: '#F79E1B', opacity: 0.95 }}></div>
                  </div>
                  {/* JCB */}
                  <div className="flex items-center justify-center px-3 py-1.5 rounded-md bg-white shadow-sm border" style={{ borderColor: '#c8a96e', minWidth: '60px', height: '32px' }}>
                    <span className="font-extrabold text-sm" style={{ color: '#003087', fontFamily: 'Arial, sans-serif' }}>JCB</span>
                  </div>
                  {/* American Express */}
                  <div className="flex items-center justify-center px-3 py-1.5 rounded-md shadow-sm border" style={{ borderColor: '#c8a96e', backgroundColor: '#2E77BC', minWidth: '60px', height: '32px' }}>
                    <span className="font-extrabold text-xs text-white" style={{ fontFamily: 'Arial, sans-serif', letterSpacing: '0.5px' }}>AMEX</span>
                  </div>
                </div>
              </div>

              {/* PAY.JP 安全決済バッジ */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 flex-wrap">
                {/* PAY.JP バッジ */}
                <div
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border"
                  style={{
                    backgroundColor: 'rgba(255,248,230,0.9)',
                    borderColor: '#c8a96e',
                    boxShadow: '0 1px 4px rgba(139,37,0,0.08)',
                  }}
                >
                  <div
                    className="w-7 h-7 flex items-center justify-center rounded-md flex-shrink-0"
                    style={{ backgroundColor: '#00B4D8' }}
                  >
                    <span className="text-white font-black text-xs" style={{ fontFamily: 'Arial, sans-serif', letterSpacing: '-0.5px' }}>P</span>
                  </div>
                  <div className="flex flex-col leading-tight">
                    <span className="font-bold text-xs" style={{ color: '#00B4D8', fontFamily: 'Arial, sans-serif' }}>PAY.JP</span>
                    <span className="text-xs" style={{ color: '#6b4c2a' }}>
                      {i18n.language === 'ja' ? '決済代行サービス' : 'Payment Gateway'}
                    </span>
                  </div>
                </div>

                {/* SSL暗号化バッジ */}
                <div
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border"
                  style={{
                    backgroundColor: 'rgba(255,248,230,0.9)',
                    borderColor: '#c8a96e',
                    boxShadow: '0 1px 4px rgba(139,37,0,0.08)',
                  }}
                >
                  <div className="w-7 h-7 flex items-center justify-center flex-shrink-0" style={{ color: '#2e7d32' }}>
                    <i className="ri-shield-check-fill text-xl"></i>
                  </div>
                  <div className="flex flex-col leading-tight">
                    <span className="font-bold text-xs" style={{ color: '#2e7d32' }}>SSL 暗号化通信</span>
                    <span className="text-xs" style={{ color: '#6b4c2a' }}>
                      {i18n.language === 'ja' ? 'カード情報は安全に保護' : 'Card info securely protected'}
                    </span>
                  </div>
                </div>

                {/* PCI DSS バッジ */}
                <div
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border"
                  style={{
                    backgroundColor: 'rgba(255,248,230,0.9)',
                    borderColor: '#c8a96e',
                    boxShadow: '0 1px 4px rgba(139,37,0,0.08)',
                  }}
                >
                  <div className="w-7 h-7 flex items-center justify-center flex-shrink-0" style={{ color: '#8B2500' }}>
                    <i className="ri-lock-2-fill text-xl"></i>
                  </div>
                  <div className="flex flex-col leading-tight">
                    <span className="font-bold text-xs" style={{ color: '#8B2500' }}>PCI DSS 準拠</span>
                    <span className="text-xs" style={{ color: '#6b4c2a' }}>
                      {i18n.language === 'ja' ? '国際セキュリティ基準対応' : 'International security standard'}
                    </span>
                  </div>
                </div>
              </div>

              {/* 補足テキスト */}
              <p className="text-xs text-center" style={{ color: '#9a7a50' }}>
                {i18n.language === 'ja'
                  ? 'カード情報はPAY.JPが安全に管理します。当サイトにカード番号が保存されることはありません。'
                  : 'Card information is securely managed by PAY.JP. Your card number is never stored on this site.'}
              </p>
            </div>
          </div>

          {/* コピーライト */}
          <div className="mt-6 pt-4" style={{ borderTop: '1px solid #c8a96e' }}>
            <p className="text-center text-sm" style={{ color: '#6b4c2a' }}>
              © 2025 KOMINKA - Magome Showa Retro House. {t('common:footer.rights')}
            </p>
          </div>
        </div>
      </div>

      {/* 下部 市松模様装飾ライン */}
      <div className="w-full h-4 flex overflow-hidden">
        {Array.from({ length: 80 }).map((_, i) => (
          <div
            key={i}
            className="flex-shrink-0 w-8 h-4"
            style={{
              backgroundColor: i % 2 === 0 ? '#c8a96e' : '#8B2500',
            }}
          />
        ))}
      </div>
    </footer>
  );
}